/**
 * Auth storage using sessionStorage so the session is per-tab.
 * Opening the app in a new tab (same browser) will not have the token and will show the login page.
 */
const KEY_TOKEN = "token";
const KEY_USER = "user";
const KEY_EMAIL = "Email";
const KEY_USERNAME = "Username";
const KEY_ROLE = "Role";

function get(key) {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function set(key, value) {
  try {
    sessionStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function remove(key) {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
}

export function getToken() {
  return get(KEY_TOKEN);
}

/** Login/register responses may use several token field names (incl. nested `data` wrappers). */
export function pickAccessToken(payload) {
  if (!payload || typeof payload !== "object") return null;
  const keys = [
    "token",
    "accessToken",
    "access_token",
    "jwt",
    "Jwt",
    "id_token",
    "bearerToken",
    "BearerToken",
    "authToken",
    "AuthToken",
  ];
  for (const k of keys) {
    const t = payload[k];
    if (t != null && String(t).trim()) return String(t).trim();
  }
  return null;
}

/**
 * Flattens common API shapes: `{ data: { token, user } }`, `{ Data: ... }`, `{ result: ... }`.
 * Use after `POST /login` so we always persist token + user when the backend nests them.
 */
export function parseLoginResponse(raw) {
  if (!raw || typeof raw !== "object") return { token: null, user: null };
  const layers = [raw, raw.data, raw.Data, raw.result, raw.Result].filter((x) => x && typeof x === "object");
  let token = null;
  let user = null;
  for (const layer of layers) {
    const t = pickAccessToken(layer);
    if (t) token = t;
    const u = layer.user ?? layer.User ?? layer.userData ?? layer.UserData;
    if (u && typeof u === "object") user = u;
  }
  if (!token) token = pickAccessToken(raw);
  if (!user) user = raw.user ?? raw.User ?? null;
  return { token, user };
}

export function getUser() {
  const raw = get(KEY_USER);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** Company id from the post-selection session user (JWT-backed). */
export function getCompanyId() {
  const user = getUser();
  const id = user?.companyId || user?.CompanyId || null;
  return id != null && String(id).trim() !== "" ? String(id) : null;
}

export function getEmail() {
  return get(KEY_EMAIL);
}

export function getUsername() {
  return get(KEY_USERNAME);
}

const MS_ROLE_CLAIM = "http://schemas.microsoft.com/ws/2008/06/identity/claims/role";

function decodeJwtPayload(token) {
  if (!token || typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length < 2) return null;
  try {
    let b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    while (b64.length % 4) b64 += "=";
    if (typeof atob === "undefined") return null;
    return JSON.parse(atob(b64));
  } catch {
    return null;
  }
}

/** Role claim from JWT when the login `user` object omits UserRoles (common in production). */
export function getRoleFromToken(token) {
  const p = decodeJwtPayload(token);
  if (!p || typeof p !== "object") return "";
  const tryStr = (v) => (typeof v === "string" && v.trim() ? v.trim().toUpperCase() : "");
  const ms = p[MS_ROLE_CLAIM];
  if (typeof ms === "string") {
    const s = tryStr(ms);
    if (s) return s;
  }
  if (Array.isArray(ms) && ms.length) {
    const s = tryStr(ms[0]);
    if (s) return s;
  }
  const candidates = [p.role, p.Role, p.roles, p.Roles];
  for (const c of candidates) {
    if (typeof c === "string") {
      const s = tryStr(c);
      if (s) return s;
    }
    if (Array.isArray(c) && c.length) {
      const s = tryStr(typeof c[0] === "string" ? c[0] : c[0]?.name || c[0]?.Name);
      if (s) return s;
    }
  }
  return "";
}

/**
 * Role string from stored user JSON (API shapes differ: UserRoles / userRoles, JWT-only login, etc.).
 */
export function getRoleFromUser(user) {
  if (!user || typeof user !== "object") return "";
  const roles = user.UserRoles ?? user.userRoles;
  if (Array.isArray(roles) && roles.length > 0) {
    const first = roles[0];
    if (typeof first === "string") return first.trim().toUpperCase();
    const n =
      first?.RoleName ??
      first?.roleName ??
      first?.Name ??
      first?.name;
    if (n != null && String(n).trim()) return String(n).trim().toUpperCase();
  }
  if (user.RoleName != null && String(user.RoleName).trim()) return String(user.RoleName).trim().toUpperCase();
  if (user.roleName != null && String(user.roleName).trim()) return String(user.roleName).trim().toUpperCase();
  if (typeof user.role === "string" && user.role.trim()) return user.role.trim().toUpperCase();
  if (user.Role != null && String(user.Role).trim()) return String(user.Role).trim().toUpperCase();
  return "";
}

/**
 * Role for guards: session `Role` key, then `user`, then JWT (must stay aligned with login redirect).
 */
export function getRole() {
  const stored = get(KEY_ROLE);
  if (stored != null && String(stored).trim()) return String(stored).trim().toUpperCase();
  const fromUser = getRoleFromUser(getUser());
  if (fromUser) return fromUser;
  return getRoleFromToken(getToken()) || "";
}

/**
 * Set auth data after login. Pass an object with token, user (object), Email, Username, Role.
 * @returns {boolean} true if all writes succeeded (e.g. not in restricted private mode)
 */
export function setAuth(data) {
  let ok = true;
  if (data.token != null) ok = set(KEY_TOKEN, data.token) && ok;
  if (data.user != null) {
    const user = { ...data.user };
    if (user.companyId == null && data.companyId != null) user.companyId = data.companyId;
    if (user.companyName == null && data.companyName != null) user.companyName = data.companyName;
    ok = set(KEY_USER, JSON.stringify(user)) && ok;
  }
  if (data.Email != null) ok = set(KEY_EMAIL, data.Email) && ok;
  if (data.Username != null) ok = set(KEY_USERNAME, data.Username) && ok;
  let role = data.Role != null ? String(data.Role).trim() : "";
  if (!role && data.user) role = getRoleFromUser(data.user);
  if (!role && data.token) role = getRoleFromToken(data.token);
  if (role) ok = set(KEY_ROLE, role) && ok;
  return ok;
}

export function clearAuth() {
  remove(KEY_TOKEN);
  remove(KEY_USER);
  remove(KEY_EMAIL);
  remove(KEY_USERNAME);
  remove(KEY_ROLE);
}
